import Router from 'next/router';
import { useState } from 'react';
import { useAchievementTypes, useDateNow, useReduxState } from 'hooks';
import {
  getMessageWithPlaceholders,
  localeLanguageCodes
} from 'shared-locales';
import { getFormattedDate } from 'lib/common';
import { Avatar, Button } from 'components/common';
import { CoursePicture } from 'components/courses/common';
import {
  PathStepTileAchievement,
  PathStepTileCalendarEvent,
  PathStepTileCourse,
  PathStepTileItemStatus
} from '.';

import type {
  AchievementProgress,
  CalendarEventProgress,
  CourseProgress,
  PathStepProgress,
  ResolvedItemInPathStep
} from 'shared-types';

interface Props {
  availableStepItemAchievementIds?: number[];
  deadline: string;
  isCompleted: boolean;
  isMandatory: boolean;
  items: ResolvedItemInPathStep[];
  progress: PathStepProgress['steps'][0];
  stepAvailable?: boolean;
  stepEnabled?: boolean;
  stepNumber: number;
}

const PathStepTile = ({
  availableStepItemAchievementIds,
  deadline,
  isCompleted,
  isMandatory,
  items,
  progress,
  stepAvailable,
  stepEnabled,
  stepNumber
}: Props) => {
  const { language, messages, theme, user } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const { dateNow } = useDateNow();

  const [selectedItemIndex, setSelectedItemIndex] = useState(
    items?.length > 1 || !items?.length ? null : 0
  );

  const selectedItem = items?.[selectedItemIndex];
  const stepProgress = selectedItem
    ? progress?.['itemProgressByTypeId']?.[
        `${selectedItem.type}${selectedItem.id}`
      ]
    : null;

  const handleClick = () => {
    if (selectedItemIndex === null) return;

    if (selectedItem.type === 'achievement') {
      Router.push(`/achievements/${selectedItem.achievement.id}`);
    } else if (selectedItem.type === 'course') {
      Router.push(`/courses/${selectedItem.course.slug}`);
    } else if (selectedItem.type === 'event') {
      Router.push(`/calendar/${selectedItem.event.id}`);
    }
  };

  const handleSelectedItemBack = (ev) => {
    ev.stopPropagation();
    setSelectedItemIndex(null);
  };

  const getItemTypeString = (item: typeof selectedItem) => {
    return item.type === 'course'
      ? messages.commonCourse
      : item.type === 'achievement'
      ? achievementTypes[item.achievement.type].title
      : item.type === 'event'
      ? messages.eventCalendarEvent
      : '';
  };

  const isTileLinkActive =
    user?.roles.includes('superadmin') || (stepAvailable && stepEnabled);

  return (
    <div
      className={`step ${isTileLinkActive ? 'step-active' : ''}`}
      onClick={isTileLinkActive ? handleClick : null}
    >
      <div className="header">
        <div className="header-info">
          {items?.length > 1 && selectedItemIndex !== null ? (
            <Button
              category="lighter"
              icon="arrow-left"
              isRounded={true}
              size="small"
              onClick={handleSelectedItemBack}
            />
          ) : (
            <div className="header-number">
              <Avatar fontSize="1.4rem">{stepNumber}</Avatar>
            </div>
          )}
          {items?.length ? (
            <div className="header-details">
              <div className="header-type">
                {items?.length > 1
                  ? getMessageWithPlaceholders({
                      message: messages.commonNumberOfAlternatives,
                      placeholders: { numberOfAlternatives: items.length }
                    })
                  : getItemTypeString(selectedItem)}

                <span style={{ margin: '0 7px' }}>{'•'}</span>

                <span className="header-mandatory">
                  {isMandatory
                    ? messages.commonMandatory
                    : messages.commonOptional}
                </span>
              </div>
              <div
                className={`header-deadline ${
                  isCompleted || !deadline
                    ? ''
                    : Date.parse(deadline) < dateNow
                    ? 'header-deadline-passed'
                    : 'header-deadline-upcoming'
                }`}
              >
                {deadline
                  ? `${messages.courseTargetSchedule}: ${getFormattedDate({
                      date: deadline
                    })}`
                  : messages.courseTargetScheduleNone}
              </div>
            </div>
          ) : (
            <div className="header-details">
              <div className="header-empty">{messages.commonNoContent}</div>
            </div>
          )}
        </div>
      </div>

      {items?.length && selectedItemIndex === null ? (
        <div className="alternatives">
          {items.map((item, i) => {
            let title =
              item.type === 'course'
                ? item.course.title
                : item.type === 'achievement'
                ? item.achievement.title
                : item.type === 'event'
                ? item.event.title
                : messages.commonTitle;

            const progressData =
              progress?.itemProgressByTypeId?.[`${item.type}${item.id}`];

            // handle course language version specific title
            if (item.type === 'course') {
              const preferredLanguageVersion =
                (progressData as CourseProgress)?.completion
                  ?.courseLanguageVersion || localeLanguageCodes[language];

              const languageVersion = item.course.languageVersions?.[
                preferredLanguageVersion
              ]?.isPublished
                ? item.course.languageVersions?.[preferredLanguageVersion]
                : null;

              title = languageVersion?.courseTitle || title;
            }

            return (
              <button
                type="button"
                className="alternatives-item"
                key={item.id}
                onClick={() => setSelectedItemIndex(i)}
              >
                <div className="alternatives-item-icon">
                  {item.type === 'course' ? (
                    <div className="alternatives-item-icon-course">
                      <CoursePicture
                        position={item.course.picturePosition}
                        size={480}
                        url={item.course.picture}
                      />
                    </div>
                  ) : item.type === 'achievement' ? (
                    <Avatar
                      icon={achievementTypes[item.achievement.type].icon}
                    />
                  ) : item.type === 'event' ? (
                    <Avatar icon={['far', 'calendar-days']} />
                  ) : null}
                </div>
                <div className="alternatives-item-info">
                  <div className="alternatives-item-title">{title}</div>
                  <div className="alternatives-item-type">
                    {getItemTypeString(item)}
                  </div>
                </div>
                <div className="alternatives-item-status">
                  {item.type === 'achievement' ? (
                    <PathStepTileItemStatus
                      progressData={progressData as AchievementProgress}
                      type={item.type}
                    />
                  ) : item.type === 'course' ? (
                    <PathStepTileItemStatus
                      progressData={progressData as CourseProgress}
                      type={item.type}
                    />
                  ) : item.type === 'event' ? (
                    <PathStepTileItemStatus
                      progressData={progressData as CalendarEventProgress}
                      startDate={item.event.startDate}
                      type={item.type}
                    />
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      ) : items?.length ? (
        <div className="item-content" tabIndex={-1}>
          {selectedItem.type === 'achievement' ? (
            <PathStepTileAchievement
              achievement={{
                ...selectedItem.achievement,
                progressData: stepProgress as AchievementProgress
              }}
              achievementIsAvailable={availableStepItemAchievementIds?.includes(
                selectedItem.achievement.id
              )}
              stepAvailable={stepAvailable}
              stepEnabled={stepEnabled}
            />
          ) : selectedItem.type === 'event' ? (
            <PathStepTileCalendarEvent
              event={{
                ...selectedItem.event,
                progressData: stepProgress as CalendarEventProgress
              }}
            />
          ) : selectedItem.type === 'course' ? (
            <PathStepTileCourse
              course={{
                ...selectedItem.course,
                progressData: stepProgress as CourseProgress
              }}
              stepAvailable={stepAvailable}
              stepEnabled={stepEnabled}
            />
          ) : null}
        </div>
      ) : null}

      <style jsx>{`
        .step {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          display: flex;
          flex-direction: column;
          transition: box-shadow 0.3s;
        }
        .step-active {
          cursor: pointer;
        }
        .step-active:focus,
        .step-active:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .header {
          align-items: center;
          display: flex;
          gap: 10px;
          justify-content: space-between;
          padding: 10px 15px;
        }
        .header-info {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .header-number {
          position: relative;
        }
        .number-completed {
          background-color: ${theme.successColor};
          border-radius: 20px;
          color: ${theme.successTextColor};
          font-size: 0.7rem;
          padding: 3px 0;
          position: absolute;
          text-align: center;
          top: -5px;
          right: -7px;
          width: 15px;
          height: 15px;
        }
        .header-details {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          margin: -3px 0 0px;
        }
        .header-type {
          font-weight: 600;
        }
        .header-deadline {
          font-weight: 400;
        }
        .header-deadline-upcoming {
          color: ${theme.warningStandaloneColor};
        }
        .header-deadline-passed {
          color: ${theme.dangerColor};
        }
        .header-actions {
          display: flex;
          gap: 10px;
        }
        .header-empty {
          font-style: italic;
          padding-top: 2px;
        }
        .alternatives {
          border-top: 1px solid ${theme.lightGreyDark};
          min-height: 350px;
        }
        .alternatives-item {
          all: unset;
          outline: revert;
          width: 100%;
          box-sizing: border-box;
          align-items: center;
          border-bottom: 1px solid ${theme.lightGrey};
          cursor: pointer;
          display: flex;
          gap: 10px;
          padding: 10px 15px;
        }
        .alternatives-item:not(:first-child) {
          //border-top: 1px solid ${theme.lightGrey};
        }
        .alternatives-item-icon {
          flex-shrink: 0;
          position: relative;
        }
        .alternatives-item-icon-course {
          border-radius: 6px;
          overflow: hidden;
          position: relative;
          height: 28px;
          width: 28px;
        }
        .alternatives-item-info {
          flex-grow: 1;
        }
        .alternatives-item-title {
          color: ${theme.textColorLight};
          display: -webkit-box;
          font-size: 1.4rem;
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }
        .alternatives-item-type {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 400;
        }
        .alternatives-item-status {
          color: ${theme.textColorLight};
          flex-shrink: 0;
          font-size: 1.2rem;
        }
        .alternatives-item:focus .alternatives-item-title,
        .alternatives-item:hover .alternatives-item-title {
          color: ${theme.textColor};
        }
        .image-container {
          background-color: ${theme.lightGreyDarkActive};
          height: 150px;
          max-width: 100%;
          position: relative;
        }
        .image {
          height: 100%;
          width: 100%;
        }
        .step-content {
          padding: 15px;
        }
        .fallback-icon {
          align-items: center;
          color: ${theme.textColorLighter};
          display: flex;
          justify-content: center;
          height: 100%;
          width: 100%;
        }
        .alternative-indication {
          position: absolute;
          bottom: 5px;
          left: 5px;
        }
        .card-info {
          color: ${theme.textColorLighter};
          display: flex;
          flex-direction: column;
          gap: 10px;
          height: 200px;
          padding: 10px 18px 6px 18px;
        }
        .title {
          font-weight: 700;
          margin-bottom: -10px;
        }
        .last-accessed {
          font-size: 1.5rem;
          width: max-content;
        }
        .alternative-count {
          font-size: 1.6rem;
          font-weight: 600;
          margin-top: auto;
        }
        .change-buttons {
          height: 100%;
          opacity: 25%;
        }
        .change-buttons:hover {
          opacity: 75%;
        }
        .previous-item {
          position: absolute;
          top: 0;
          left: 0;
        }
        .next-item {
          position: absolute;
          top: 0;
          right: 0;
        }
      `}</style>
    </div>
  );
};

export default PathStepTile;
